import type { DependencyList } from "react"
import { useCallback, useEffect } from "react"
import { useNavigate } from "@tanstack/react-router"
import { create } from "zustand" // move to lib/states

type PageType =
	| "threads"
	| "thread-details"
	| "opportunities"
	| "opportunities-detail"
	| "issues"
	| "issues-detail"
	| "projects"
	| "projects-detail"
	| "customers"
	| "customers-detail"
	| "people"
	| "contacts-detail"
	| "members-detail"
	| "edit-requests"
	| "support-portal"
	| "search"

interface NavigationStateEntry {
	type: PageType
	url: string
}

interface NavigationState {
	pages: Array<NavigationStateEntry>
	setPages: (value: Array<NavigationStateEntry>) => void
}

export const useNavigationState = create<NavigationState>()((set) => ({
	pages: [],
	setPages: (value) => {
		set({ pages: value })
	},
}))

export function useBackNavigation() {
	const { pages } = useNavigationState()
	const navigate = useNavigate()
	const goBack = useCallback(
		(fallbackRoute: string, replace?: boolean) => {
			// try to take the second to last element
			// e.g. insights, go to insight detail => [insights, insight-detail]
			pages.pop() // remove 'insight-detail'
			const entry = pages.pop() // go to 'insights'
			// going to 'insights' pushes it back into the array
			if (entry) {
				if (replace) {
					void navigate({ href: entry.url, replace: true })
				} else {
					void navigate({ href: entry.url })
				}
				return
			}
			if (replace) {
				void navigate({ href: fallbackRoute, replace: true })
			} else {
				void navigate({ href: fallbackRoute })
			}
		},
		[navigate, pages],
	)
	return { goBack }
}

const PAGE_TYPE_LABEL: Record<PageType, string> = {
	threads: "Inbox",
	"thread-details": "Thread",
	opportunities: "Opportunities",
	"opportunities-detail": "Opportunity",
	issues: "Feature requests",
	"issues-detail": "Feature request",
	projects: "Projects",
	"projects-detail": "Project",
	customers: "Customers",
	"customers-detail": "Customer",
	people: "People",
	"contacts-detail": "Contact",
	"members-detail": "Member",
	"edit-requests": "Requests",
	"support-portal": "Support portal",
	search: "Search",
}

export function usePreviousNavEntry(currentType: PageType) {
	const { pages } = useNavigationState()
	for (let i = pages.length - 1; i >= 0; i--) {
		const entry = pages[i]
		if (entry && entry.type !== currentType) {
			return { label: PAGE_TYPE_LABEL[entry.type], url: entry.url }
		}
	}
	return null
}

export function useSetBackNavigation(
	route: NavigationStateEntry,
	deps: DependencyList = [],
) {
	const { pages, setPages } = useNavigationState()
	useEffect(() => {
		function setLast() {
			const prev = pages[pages.length - 1]
			if (prev && prev.type === route.type) {
				// replace prev entry, e.g. when using 'next' between insight details
				const newPages = [...pages]
				newPages.pop()
				setPages([...newPages, route])
			} else if (prev) {
				setPages([...pages, route])
			} else {
				// no prev just push
				setPages([route])
			}
		}

		setLast()
		// router.events.on("routeChangeStart", setLast)

		// return () => {
		// 	router.events.off("routeChangeStart", setLast)
		// }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps)
}
